import {
  Button,
  Card,
  type ColumnDef,
  DocumentIcon,
  EmptyState,
  Inline,
  ListView,
  type ListViewDef,
  PageContent,
  PageHeader,
  type SortState,
  StatusLabel,
  Tab,
  TabList,
  Tabs,
  TextLink,
} from '@olytic/foundation';
import type { CatalogEntry, CatalogTask, GlossaryTerm } from '@olytic/foundation/catalog';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useDocumentTitle } from '../documentTitle';
import {
  ALL_SHELVES,
  browseEntries,
  entryHref,
  library,
  type LibraryTab,
  matchesQuery,
  shelfSingular,
  STATUS_LABEL,
  STATUS_TONE,
} from './libraryData';

/** An entry's name, as a link to its page. */
export function EntryLink({ entry }: { entry: Pick<CatalogEntry, 'id' | 'name'> }): JSX.Element {
  return <TextLink asChild><Link to={entryHref(entry)}>{entry.name}</Link></TextLink>;
}

/** Entries as a run of links: "Record page, List view". */
export function EntryLinks({ ids }: { ids: readonly string[] }): JSX.Element {
  const entries = library.entriesFor(ids);
  return (
    <Inline as="span" gap="xs">
      {entries.map((entry, i) => (
        <span key={entry.id}>
          <EntryLink entry={entry} />
          {i < entries.length - 1 ? ',' : ''}
        </span>
      ))}
    </Inline>
  );
}

const TABS: readonly { key: LibraryTab; label: string; shown: boolean }[] = [
  { key: 'browse', label: 'Browse', shown: true },
  { key: 'tasks', label: 'I Want to…', shown: library.hasTasks },
  { key: 'glossary', label: 'Glossary', shown: library.hasGlossary },
];

const SHOWN_TABS = TABS.filter((t) => t.shown);

/**
 * The Library: every building block Foundation offers, from whole pages down
 * to colours, in plain words, for people who don't read the Foundation repo.
 */
export function LibraryPage(): JSX.Element {
  useDocumentTitle('Library');
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab');
  const tab: LibraryTab = SHOWN_TABS.find((t) => t.key === asked)?.key ?? 'browse';

  const pickTab = (key: string) => {
    setParams(key === 'browse' ? {} : { tab: key });
  };

  let body: ReactNode;
  if (tab === 'tasks') body = <TasksTab />;
  else if (tab === 'glossary') body = <GlossaryTab />;
  else body = <BrowseTab />;

  return (
    <PageContent as="section" width="fill" className="gap-4 px-3 pb-8 pt-5 sm:px-6">
      <Card className="shrink-0 overflow-hidden">
        <PageHeader
          className={SHOWN_TABS.length > 1 ? 'border-b-0' : ''}
          leading={<DocumentIcon className="size-[46px] text-[var(--brand-accent)]" />}
          title="Library"
          subtitle={`The building blocks every Olytic app is made from, whole pages first. Foundation ${library.catalog.version}.`}
        />
        {SHOWN_TABS.length > 1 ? (
          <Tabs value={tab} onValueChange={pickTab}>
            <TabList className="border-b-0 px-6">
              {SHOWN_TABS.map((t) => <Tab key={t.key} value={t.key}>{t.label}</Tab>)}
            </TabList>
          </Tabs>
        ) : null}
      </Card>
      {body}
    </PageContent>
  );
}

// ── Browse ─────────────────────────────────────────────────────────────────

const COLUMNS: ColumnDef<CatalogEntry>[] = [
  { key: 'code', label: 'Code', sortable: true, width: 80, figures: true, get: (e) => e.code },
  {
    key: 'name',
    label: 'Name',
    primary: true,
    sortable: true,
    width: 240,
    get: (e) => e.name,
    href: (e) => entryHref(e),
  },
  { key: 'purpose', label: 'What It Is For', get: (e) => e.purpose },
  {
    key: 'shelf',
    label: 'Shelf',
    sortable: true,
    width: 150,
    hideOnMobile: true,
    get: (e) => shelfSingular(e.shelf),
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    width: 130,
    hideOnMobile: true,
    get: (e) => STATUS_LABEL[e.status],
    render: (e) => <StatusLabel tone={STATUS_TONE[e.status]}>{STATUS_LABEL[e.status]}</StatusLabel>,
  },
];

/** One view per shelf, Blueprints first, then every shelf at once. */
const VIEWS: ListViewDef<CatalogEntry>[] = [
  ...library.shelves.map((s) => ({ id: s.id, label: s.name })),
  { id: ALL_SHELVES, label: 'Every Shelf' },
];

/** A shelf's view leaves out the Shelf column; every shelf at once shows it. */
const columnsFor = (shelf: string) =>
  COLUMNS.map((c) => c.key).filter((key) => key !== 'shelf' || shelf === ALL_SHELVES);

const AREA_FILTER = {
  key: 'area',
  label: 'Area',
  inline: true,
  options: library.domains.map((d) => ({ value: d.id, label: d.name })),
};

function BrowseTab(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const shelf = VIEWS.some((v) => v.id === params.get('shelf')) ? (params.get('shelf') as string) : library.shelves[0]?.id ?? ALL_SHELVES;
  const domain = library.domains.find((d) => d.id === params.get('area'))?.id;
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | undefined>(undefined);

  const update = (next: { shelf?: string; area?: string | undefined }) => {
    const merged = { shelf, area: domain, ...next };
    const out: Record<string, string> = {};
    if (merged.shelf && merged.shelf !== library.shelves[0]?.id) out.shelf = merged.shelf;
    if (merged.area) out.area = merged.area;
    setParams(out);
  };

  const rows = useMemo(() => browseEntries(library, { shelf, domain, query: search }), [shelf, domain, search]);

  const elsewhere = search.trim() && shelf !== ALL_SHELVES
    ? browseEntries(library, { shelf: ALL_SHELVES, domain, query: search }).length
    : 0;
  const shelfInfo = library.shelfOf(shelf as CatalogEntry['shelf']);

  return (
    <ListView
      objectLabel="Building blocks"
      objectIcon={<DocumentIcon />}
      columns={COLUMNS}
      visibleColumns={columnsFor(shelf)}
      columnPicker={false}
      rows={rows}
      getRowId={(e) => e.id}
      mode="client"
      views={VIEWS}
      activeViewId={shelf}
      onViewChange={(id) => update({ shelf: id })}
      filters={[AREA_FILTER]}
      filterValues={domain ? { area: domain } : {}}
      onFilterChange={(_key, value) => update({ area: value || undefined })}
      search={search}
      onSearchChange={setSearch}
      {...(sort ? { sort } : {})}
      onSortChange={setSort}
      selectable={false}
      pagination="none"
      onRowClick={(e) => navigate(entryHref(e))}
      onNavigate={navigate}
      empty={
        <EmptyState
          icon={<DocumentIcon />}
          title={search.trim() ? `Nothing on this shelf matches “${search.trim()}”` : 'Nothing on this shelf yet'}
          description={
            elsewhere
              ? `${elsewhere === 1 ? 'One building block' : `${elsewhere} building blocks`} on other shelves ${elsewhere === 1 ? 'matches' : 'match'}.`
              : search.trim()
                ? 'Try another word, such as “table”, “form” or “chart”.'
                : shelfInfo?.purpose
          }
          action={elsewhere ? <Button variant="outline" onClick={() => update({ shelf: ALL_SHELVES })}>Search Every Shelf</Button> : undefined}
        />
      }
    />
  );
}

// ── I want to… ─────────────────────────────────────────────────────────────

const TASK_COLUMNS: ColumnDef<CatalogTask>[] = [
  { key: 'want', label: 'I Want to…', width: 360, get: (t) => t.want },
  { key: 'entries', label: 'Use', get: (t) => t.entries.join(' '), render: (t) => <EntryLinks ids={t.entries} /> },
];

function TasksTab(): JSX.Element {
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return library.catalog.tasks.filter(
      (t) => !q || t.want.toLowerCase().includes(q) || library.entriesFor(t.entries).some((e) => matchesQuery(e, q)),
    );
  }, [search]);
  return (
    <ListView
      objectLabel="Tasks"
      objectIcon={<DocumentIcon />}
      columns={TASK_COLUMNS}
      rows={rows}
      getRowId={(t) => t.id}
      mode="client"
      columnPicker={false}
      search={search}
      onSearchChange={setSearch}
      selectable={false}
      pagination="none"
      empty={<EmptyState icon={<DocumentIcon />} title={`Nothing matches “${search.trim()}”`} description="Try another word." />}
    />
  );
}

// ── Glossary ───────────────────────────────────────────────────────────────

const TERM_COLUMNS: ColumnDef<GlossaryTerm>[] = [
  { key: 'term', label: 'Term', sortable: true, width: 220, get: (t) => t.term },
  { key: 'definition', label: 'Meaning', get: (t) => t.definition },
  {
    key: 'entries',
    label: 'See',
    hideOnMobile: true,
    get: (t) => t.entries.join(' '),
    render: (t) => <EntryLinks ids={t.entries} />,
  },
];

function GlossaryTab(): JSX.Element {
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...library.catalog.glossary]
      .sort((a, b) => a.term.localeCompare(b.term))
      .filter((t) => !q || `${t.term} ${t.definition}`.toLowerCase().includes(q));
  }, [search]);
  return (
    <ListView
      objectLabel="Glossary"
      objectIcon={<DocumentIcon />}
      columns={TERM_COLUMNS}
      rows={rows}
      getRowId={(t) => t.id}
      mode="client"
      columnPicker={false}
      search={search}
      onSearchChange={setSearch}
      selectable={false}
      pagination="none"
      empty={<EmptyState icon={<DocumentIcon />} title={`No term matches “${search.trim()}”`} description="Try another word." />}
    />
  );
}
